import type { User } from '../types';

export type UserProfile = Omit<User, 'password'>;

const STORAGE_KEY = 'amplifyChatUserProfiles';

const isBrowser = typeof window !== 'undefined';

const withDefaultWorkspaces = (profile: Partial<UserProfile>): UserProfile => {
  return {
    id: profile.id ?? '',
    name: profile.name ?? 'User',
    email: profile.email ?? '',
    username: profile.username ?? null,
    first_name: profile.first_name ?? null,
    last_name: profile.last_name ?? null,
    dob: profile.dob ?? null,
    profile_complete: profile.profile_complete ?? false,
    workspaces: Array.isArray(profile.workspaces) ? profile.workspaces : [],
  };
};

export const loadUserProfiles = (): UserProfile[] => {
  if (!isBrowser) {
    return [];
  }
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .filter(item => typeof item === 'object' && item !== null)
      .map(item => withDefaultWorkspaces(item as Partial<UserProfile>));
  } catch (error) {
    console.error('Failed to parse stored user profiles:', error);
    return [];
  }
};

export const saveUserProfiles = (profiles: UserProfile[]) => {
  if (!isBrowser) {
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles));
};

export const upsertUserProfile = (profile: UserProfile): UserProfile[] => {
  const existing = loadUserProfiles();
  const index = existing.findIndex(p => p.id === profile.id);
  let next: UserProfile[];
  if (index === -1) {
    next = [...existing, withDefaultWorkspaces(profile)];
  } else {
    next = [...existing];
    next[index] = withDefaultWorkspaces({ ...existing[index], ...profile });
  }
  saveUserProfiles(next);
  return next;
};

export const removeUserProfile = (userId: string): UserProfile[] => {
  const existing = loadUserProfiles();
  const next = existing.filter(profile => profile.id !== userId);
  saveUserProfiles(next);
  return next;
};
